import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { InputTextField } from '@tempmee/react';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import useDebouncedValue from '../debounceHook';

/**
 * A filter-card search input that keeps typing responsive: the keystrokes land
 * in local state and only the settled value is reported to the caller.
 *
 * Every list page had grown its own copy of this — a `useState`, a
 * `debounceHook`, and a `useEffect` whose dependency array deliberately omitted
 * half of what it read. Reporting is driven from a ref of the last value
 * actually sent, which also means mounting no longer re-reports the value the
 * filter already holds (each of those was a wasted refetch).
 */
export const DebouncedSearchField = ({
  label,
  value,
  onChange,
  placeholder,
  delay,
  icon,
  type,
}) => {
  const [text, setText] = useState(value ?? '');
  const debounced = useDebouncedValue(text, delay);
  const reported = useRef(value ?? '');

  useEffect(() => {
    if (debounced === reported.current) return;
    reported.current = debounced;
    onChange(debounced);
  }, [debounced, onChange]);

  return (
    <InputTextField
      aria-label={label}
      placeholder={placeholder ?? label}
      type={type}
      // Not cosmetic, and not removable: InputTextField makes the <input> a
      // flex item, so its `min-width: auto` resolves to the ~20-character
      // intrinsic width an <input> carries by default. In FiltersCard's
      // `minmax(220px, 1fr)` columns the slot is 176px, so at the 220px floor
      // the input cannot shrink to fit and its text renders past the field's
      // background and underline — GOTU-5861, reported against a long email.
      // `size=1` drops that intrinsic width to one character; `flex-1` still
      // grows the field to fill the column, so nothing looks narrower.
      //
      // Measured (input width vs a 176px slot): Firefox 230px, Chrome 186px —
      // which is why this reads as Firefox-only. Chrome overflows by 10px and
      // is easy to miss.
      //
      // The tidier fix is `min-width: 0` on the input itself, but className on
      // InputTextField lands on the wrapper div, not the input — so that one
      // has to happen in @tempmee/react (`inputBaseClass`).
      size={1}
      leftIcon={icon ?? <OutlineSearchIcon aria-hidden className="size-5" />}
      value={text}
      onChange={(event) => setText(event.target.value)}
    />
  );
};

DebouncedSearchField.propTypes = {
  /** Accessible name, and the default placeholder. */
  label: PropTypes.string.isRequired,
  /** Current value held by the filter state; seeds the field. */
  value: PropTypes.string,
  /** Called with the settled text — never with the value it was seeded with. */
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  delay: PropTypes.number,
  /** Leading icon; defaults to the search glyph. */
  icon: PropTypes.node,
  type: PropTypes.string,
};

DebouncedSearchField.defaultProps = {
  value: '',
  placeholder: undefined,
  delay: 1000,
  icon: undefined,
  type: 'text',
};

export default DebouncedSearchField;
